'use client';

import { VideoCard } from '@/cards/VideoCard';
import { logger } from '@/utils/loggerUtils';
import { AssetType } from '@vimond/services/content-discovery';
import { Suspense, useEffect, useRef, useState } from 'react';

const ITEMS_PER_PAGE = 4;

type Props = {
  assets?: AssetType[];
  fetchMoreAssets?: (page: number) => Promise<AssetType[]>;
  title?: string;
};

export const AssetGrid = ({
  assets: initialAssets = [],
  fetchMoreAssets,
  title,
}: Props & React.ComponentPropsWithoutRef<'section'>) => {
  const [assets, setAssets] = useState<AssetType[]>(initialAssets);
  const [page, setPage] = useState(2);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadMoreAssets = async (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (fetchMoreAssets && target?.isIntersecting && hasMore && !loading) {
        setLoading(true);
        try {
          const newAssets = await fetchMoreAssets(page);
          if (newAssets.length < ITEMS_PER_PAGE) {
            setHasMore(false);
          }
          setAssets((prev) => [...prev, ...newAssets]);
          setPage((prev) => prev + 1);
        } catch (error) {
          logger.error('Error fetching more assets:', error);
          setHasMore(false);
        } finally {
          setLoading(false);
        }
      }
    };

    const observer = new IntersectionObserver(loadMoreAssets, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.disconnect();
      }
    };
  }, [page, hasMore, loading, fetchMoreAssets]);

  return (
    <section className="relative mx-3 p-4 lg:mx-10">
      <h1 className="my-2 text-2xl font-bold">{title}</h1>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {assets?.map((asset) => (
          <Suspense key={asset.id}>
            <VideoCard asset={asset} key={asset.id} />
          </Suspense>
        ))}
      </div>
      <div ref={observerTarget} className="h-10 w-full" aria-hidden="true" />
      {loading && <div className="mt-4 text-center">Loading more...</div>}
    </section>
  );
};
